import { Button } from '@creditornot/cb-button';
import { FormHeader, FormRow, InputContainer } from '@creditornot/cb-form';
import { MagnifyingGlass } from '@creditornot/cb-icons';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { Spinner } from '@creditornot/cb-progress';
import { SegmentedControl } from '@creditornot/cb-segmented-control';
import { Switch } from '@creditornot/cb-switch';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime, Interval } from 'luxon';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { APIErrorNotification, WoltModal } from 'common/components';
import ErrorToastContent from 'common/components/ErrorToastContent';
import { useDebounce } from 'common/hooks';
import { Typography as T } from 'common/typography';
import { isDefined } from 'common/utils';
import { MenuMetaContext } from 'modules/menu/MenuMetaContext';

import { MenuCopyVenueList } from './MenuCopyVenueList';
import { useMenuSyncContext } from './MenuSyncContext';
import ScheduleDatePicker from './ScheduleDatePicker';
import { fetchPaginatedMerchantVenues, searchMerchantVenues, startMenuSync } from './api';
import { MenuSyncPayload } from './types';
import { MenuLocaleContext } from '../MenuLocaleContext';
import { reportMenuSyncActionInitiate } from '../behavioral';
import { DeprecationNote } from '../components/DeprecationNote';

type Props = {
  venueSlug: string;
};

const Root = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background-color: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  flex-direction: column;
  max-height: 100vh;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 22px 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectedCount = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
  margin-inline-end: 16px;
`;

const SearchInput = styled(Input)`
  width: 240px;
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
  padding-block-start: 32px;
`;

const ErrorContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;
`;

const ListFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
  padding-inline-end: 100px;
`;

const SettingsModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: space-between;
`;

const SettingsModal = styled(WoltModal)`
  min-width: 320px;
  max-width: 640px;
  max-height: 95%;
  border-radius: 8px;
  background-color: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

const StyledFormRow = styled(FormRow)`
  align-items: center;
`;

const SwitchContainer = styled(InputContainer)``;

const ExplanationContainer = styled.div`
  padding: 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const AdvancedSettingHeader = styled(FormHeader)`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const TooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  ${typographyCss.Small}
`;

const getScheduleIntervalInDays = (scheduledTime: number, timezone: string) => {
  const diffInterval = Interval.fromDateTimes(
    DateTime.now().setZone(timezone),
    DateTime.fromMillis(scheduledTime, { zone: timezone }),
  );
  return diffInterval.length('days');
};

const MenuSyncPage = ({ venueSlug }: Props) => {
  const intl = useIntl();
  const history = useHistory();
  const { menuId, merchantId } = useContext(MenuMetaContext);
  const { timezone } = useContext(MenuLocaleContext);
  const { refetchSyncInfo } = useMenuSyncContext();

  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState<number | null>(null);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [keepPriceInfo, setKeepPriceInfo] = useState(false);
  const [keepAvailabilityInfo, setKeepAvailabilityInfo] = useState(false);
  const [searching, setSearching] = useState(false);

  const {
    data: allData,
    isLoading: isLoadingAllData,
    error: allDataError,
  } = useQuery(
    [`/v2/merchant-admin/merchants/${merchantId}/venues`],
    /*
      Temporary page size of 1000, so that 99.99% of the time we don't need pagination
      TODO: add infinite list pagination & fetch everything when clicking the "Select all" button
    */
    async () => fetchPaginatedMerchantVenues(merchantId, 1000, nextPageToken),
    {
      onSuccess: data => setNextPageToken(data.next_page_token),
      retry: false,
    },
  );

  const {
    data: searchData,
    mutate: searchVenues,
    error: searchError,
  } = useMutation(['search-merchant-venues', merchantId], () => searchMerchantVenues(merchantId, searchInput), {
    onSettled: () => setSearching(false),
  });

  const debouncedHandleSearch = useDebounce(searchVenues, 300);
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setSearching(true);
    setSearchInput(searchValue);
    debouncedHandleSearch(searchValue);
  };

  const handleSearchClear = () => {
    setSearching(true);
    setSearchInput('');
    debouncedHandleSearch('');
  };

  const [selectedMenuIds, setSelectedMenuIds] = useState<string[]>([]);

  const { mutate: handleSubmit, isLoading: submitting } = useMutation(
    () => {
      const payload: MenuSyncPayload = {
        target_ids: [...selectedMenuIds],
        scheduled_at:
          isScheduled && scheduledAt ? DateTime.fromMillis(scheduledAt, { zone: timezone }).toUTC().toISO() : undefined,
        options: {
          inspect_availability_fields: !keepAvailabilityInfo,
          inspect_price_fields: !keepPriceInfo,
        },
      };
      return startMenuSync(menuId, payload);
    },
    {
      onMutate: () => {
        const scheduled = isScheduled && scheduledAt;
        const scheduledInAdvanceDays = scheduled ? getScheduleIntervalInDays(scheduledAt, timezone) : undefined;
        reportMenuSyncActionInitiate(
          scheduled ? 'start-scheduled' : 'start-now',
          selectedMenuIds.length,
          allData
            ? allData.venues
                .map(venue => venue.menu_id || undefined)
                .filter(isDefined)
                .filter(id => id !== menuId).length
            : -1,
          scheduledInAdvanceDays,
          keepAvailabilityInfo,
          keepPriceInfo,
        );
      },
      onSuccess: async () => {
        await refetchSyncInfo();
        history.push(`/venue/${venueSlug}/menu-editor`);
        toast.success(
          isScheduled && scheduledAt ? (
            <FormattedMessage id="menu.menu-sync.schedule-success-description" />
          ) : (
            <FormattedMessage id="menu.menu-sync.success-description" />
          ),
        );
      },
      onError: (error: any) => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
    },
  );

  const loading = isLoadingAllData || searching;
  const error = allDataError || searchError;

  const dataToShow = useMemo(() => {
    if (!!searchInput && searchData && searchData.venues.length > 0) {
      return searchData.venues.filter(venue => venue.menu_id !== null && venue.menu_id !== menuId);
    }
    if (!searchInput && allData && allData.venues) {
      return allData.venues.filter(venue => venue.menu_id !== null && venue.menu_id !== menuId);
    }
    return [];
  }, [searchInput, searchData, allData, menuId]);

  const shownMenuIds = useMemo(
    () =>
      dataToShow
        .map(venue => venue.menu_id || undefined)
        .filter(isDefined)
        .filter(id => id !== menuId && !!id),
    [dataToShow, menuId],
  );

  const venuesToDeselect = useMemo(
    () => shownMenuIds?.filter(id => selectedMenuIds.includes(id)),
    [shownMenuIds, selectedMenuIds],
  );

  const handleSelectAll = useCallback(() => {
    if (shownMenuIds) {
      setSelectedMenuIds(oldState => [...new Set([...oldState].concat(shownMenuIds))]);
    }
  }, [shownMenuIds]);

  const handleDeselectAll = useCallback(() => {
    if (venuesToDeselect) {
      setSelectedMenuIds(oldState => oldState.filter(id => !venuesToDeselect.includes(id)));
    }
  }, [venuesToDeselect]);

  const handleRowClick = (clickedId: string) => {
    if (selectedMenuIds.includes(clickedId)) {
      setSelectedMenuIds(oldState => oldState.filter(id => id !== clickedId));
    } else {
      setSelectedMenuIds(oldState => [...oldState, clickedId]);
    }
  };

  return (
    <Root>
      <DeprecationNote featureToBeDeprecated="Menu copy" />
      <HeaderContainer>
        <HeaderLeft>
          <T.Heading3 style={{ marginBlockEnd: '8px' }}>
            <FormattedMessage id="menu.menu-sync.select-venues-header" />
          </T.Heading3>
          <T.Body3>
            <FormattedMessage id="menu.menu-sync.select-venues-subheader" />
          </T.Body3>
        </HeaderLeft>
        <SearchInput
          icon={<MagnifyingGlass />}
          value={searchInput}
          onChange={handleSearch}
          onClearClick={handleSearchClear}
        />
      </HeaderContainer>
      {loading && (
        <SpinnerContainer>
          <Spinner size="large" />
        </SpinnerContainer>
      )}
      {!error && !loading && (
        <MenuCopyVenueList
          onRowClick={handleRowClick}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          selectedMenuIds={selectedMenuIds}
          venues={dataToShow}
        />
      )}
      {!!error && (
        <ErrorContainer>
          <APIErrorNotification error={error} style={{ width: '100%' }} />
        </ErrorContainer>
      )}
      <ListFooter>
        {selectedMenuIds.length > 0 && (
          <SelectedCount>
            <FormattedMessage id="menu.menu-sync.selected-venues-count" values={{ count: selectedMenuIds.length }} />
          </SelectedCount>
        )}
        <Button
          size="small"
          onClick={() => history.push(`/venue/${venueSlug}/menu-editor`)}
          variant="lightBlue"
          style={{ marginInlineEnd: '16px' }}
        >
          <FormattedMessage id="state.cancel" />
        </Button>
        <Button
          disabled={selectedMenuIds.length === 0 || submitting}
          loading={submitting}
          onClick={() => setShowSettingsModal(true)}
          size="small"
          variant="blue"
        >
          <FormattedMessage id="state.continue" />
        </Button>
      </ListFooter>
      <SettingsModal isOpen={showSettingsModal}>
        <ModalHeader>
          <FormattedMessage id="menu.menu-sync.settings-modal.header" />
        </ModalHeader>
        <FormRow
          label={intl.formatMessage({ id: 'menu.menu-sync.schedule-toggle-label' })}
          labelAlign="center"
          horizontalPadding
        >
          <SegmentedControl<boolean>
            options={[
              { label: intl.formatMessage({ id: 'dropdown.confirmNo' }), value: false },
              { label: intl.formatMessage({ id: 'dropdown.confirmYes' }), value: true },
            ]}
            value={isScheduled}
            onChange={value => setIsScheduled(value)}
          />
        </FormRow>
        {isScheduled && (
          <FormRow
            label={intl.formatMessage({ id: 'menu.menu-sync.schedule-date-label' })}
            labelAlign="center"
            horizontalPadding
          >
            <ScheduleDatePicker onChange={val => setScheduledAt(val)} value={scheduledAt} />
          </FormRow>
        )}
        {showAdvancedSettings && (
          <div>
            <AdvancedSettingHeader horizontalPadding>
              <FormattedMessage id="menu.importer-settings.price.header" />
            </AdvancedSettingHeader>
            <ExplanationContainer>
              <T.Body3>
                <FormattedMessage id="menu.importer-settings.price.hint" />
              </T.Body3>
            </ExplanationContainer>
            <StyledFormRow
              label={intl.formatMessage({ id: 'menu.importer-settings.price.label' })}
              horizontalPadding
              info={
                <TooltipContainer>
                  <FormattedMessage id="menu.importer-settings.price.tooltip-header" />
                  <li>
                    <FormattedMessage id="menu-item.price" />
                  </li>
                  <li>
                    <FormattedMessage id="menu-item.discount" />
                  </li>
                  <li>
                    <FormattedMessage id="menu-option.choices-header" />:{' '}
                    <FormattedMessage id="menu-option.price-heading" />
                  </li>
                </TooltipContainer>
              }
            >
              <SwitchContainer>
                <Switch checked={keepPriceInfo} onChange={() => setKeepPriceInfo(!keepPriceInfo)} />
              </SwitchContainer>
            </StyledFormRow>
            <AdvancedSettingHeader horizontalPadding>
              <FormattedMessage id="menu.importer-settings.availability.header" />
            </AdvancedSettingHeader>
            <ExplanationContainer>
              <T.Body3>
                <FormattedMessage id="menu.importer-settings.availability.hint" />
              </T.Body3>
            </ExplanationContainer>
            <StyledFormRow
              label={intl.formatMessage({ id: 'menu.importer-settings.availability.label' })}
              horizontalPadding
              info={
                <TooltipContainer>
                  <FormattedMessage id="menu.importer-settings.availability.tooltip-header" />{' '}
                  <li>
                    <FormattedMessage id="menu-item.enabled" />
                  </li>
                  <li>
                    <FormattedMessage id="special.availability" />
                  </li>
                  <li>
                    <FormattedMessage id="menu-item.time-available" />
                  </li>
                  <li>
                    <FormattedMessage id="menu-item.time-visible" />
                  </li>
                  <li>
                    <FormattedMessage id="inventory-management.type" />
                  </li>
                  <li>
                    <FormattedMessage id="menu-option.choices-header" />: <FormattedMessage id="menu-item.enabled" />{' '}
                  </li>
                </TooltipContainer>
              }
            >
              <SwitchContainer>
                <Switch
                  checked={keepAvailabilityInfo}
                  onChange={() => setKeepAvailabilityInfo(!keepAvailabilityInfo)}
                />
              </SwitchContainer>
            </StyledFormRow>
          </div>
        )}
        <SettingsModalFooter style={{ paddingInlineEnd: '24px' }}>
          <Switch checked={showAdvancedSettings} onChange={() => setShowAdvancedSettings(oldValue => !oldValue)}>
            <T.Body3>
              <FormattedMessage id="menu.importer-settings.show-settings" />
            </T.Body3>
          </Switch>
          <div>
            <Button
              size="small"
              onClick={() => setShowSettingsModal(false)}
              variant="lightBlue"
              style={{ marginInlineEnd: '16px' }}
            >
              <FormattedMessage id="state.cancel" />
            </Button>
            <Button
              disabled={selectedMenuIds.length === 0 || submitting || (isScheduled && !scheduledAt)}
              loading={submitting}
              onClick={() => handleSubmit()}
              size="small"
              variant="blue"
            >
              {isScheduled ? (
                <FormattedMessage id="menu.menu-sync.submit-schedule" />
              ) : (
                <FormattedMessage id="menu.menu-sync.submit" />
              )}
            </Button>
          </div>
        </SettingsModalFooter>
      </SettingsModal>
    </Root>
  );
};

export default MenuSyncPage;
